import React, {useEffect, useCallback, RefObject} from 'react';
import {Icon, PriorityIcon} from '@priority-software/priority-style-react';
import styles from '../../styles/booleanInput.module.scss';
import {keys} from '../../../../constants/KeyCodes';
import cx from 'classnames';

type Props = {
    value: boolean;
    handleChange: (val: boolean) => void;
    inputRef: RefObject<HTMLInputElement>;
    isActive: boolean;
    isReadonly: boolean;
    searchMode: boolean;
    processEventCodes: (e: KeyboardEvent) => void;
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
};

export const BooleanInput = ({
    value = false,
    handleChange,
    inputRef,
    isActive,
    isReadonly,
    searchMode,
    processEventCodes,
    onKeyDown
}: Props) => {
    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            switch (e.key) {
                case keys.SPACE:
                    e.preventDefault();
                    !isReadonly && handleChange(!value);
                    break;
                default:
                    break;
            }
            processEventCodes(e);
        },
        [processEventCodes, handleChange, isReadonly, value]
    );

    const handleClick = () => {
        !isReadonly && handleChange(!value);
    };

    useEffect(() => {
        isActive && document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown, isActive]);

    return (
        <div
            className={styles.inputWrapper}
            onClick={handleClick}
            tabIndex={0}
            ref={inputRef}
            onKeyDown={onKeyDown}
        >
            <Icon
                skin="primary"
                icon={value && PriorityIcon.check}
                componentClasses={cx(
                    styles.checkbox,
                    {[styles.active]: !isReadonly && isActive},
                    {[styles.readonly]: isReadonly && isActive},
                    {[styles.search]: searchMode && isActive}
                )}
            />
        </div>
    );
};
